/**
 * OS10 — PROFILE CTA
 * "Comenta 'OS' — te mando o mapa."
 * 10s | 720x900 | SEM AUDIO
 */

import React from "react";
import {
  AbsoluteFill,
  useCurrentFrame,
  interpolate,
  Easing,
  Sequence,
  staticFile,
  Img,
} from "remotion";
import {CLAMP} from "../../presets/idv";

const T = {
  dark: { bg: "#0D1117", headline: "#FFFFFF", body: "#C8C4C0", muted: "#888888" },
  accent: "#00FF87",
  accentBg: "rgba(0,255,135,0.12)",
};

const FONT_SERIF = "'Playfair Display', Georgia, serif";
const FONT_MONO = "'JetBrains Mono', 'Fira Code', monospace";
const FONT_SANS = "'Inter', system-ui, sans-serif";

const SZ = { top: 70, sides: 50, bottom: 80 };
const CW = 720 - SZ.sides * 2;

const fadeIn = (frame: number, start: number, end: number) =>
  interpolate(frame, [start, end], [0, 1], { extrapolateRight: CLAMP });
const slideUp = (frame: number, start: number, end: number, dist = 20) =>
  interpolate(frame, [start, end], [dist, 0], { extrapolateRight: CLAMP, easing: Easing.out(Easing.ease) });

const GridPattern: React.FC = () => {
  const frame = useCurrentFrame();
  const opacity = 0.03 + Math.sin(frame * 0.02) * 0.005;
  return (
    <div style={{
      position: "absolute", inset: 0,
      backgroundImage: `linear-gradient(rgba(255,255,255,${opacity}) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,${opacity}) 1px, transparent 1px)`,
      backgroundSize: "40px 40px", pointerEvents: "none",
    }} />
  );
};

const CircuitPattern: React.FC = () => {
  const frame = useCurrentFrame();
  const opacity = 0.5 + Math.sin(frame * 0.02) * 0.1;
  return (
    <div style={{
      position: "absolute", inset: 0, pointerEvents: "none",
      backgroundImage: `repeating-linear-gradient(0deg, rgba(0,255,135,${opacity * 0.03}) 0px, transparent 1px, transparent 59px, rgba(0,255,135,${opacity * 0.03}) 60px), repeating-linear-gradient(90deg, rgba(0,255,135,${opacity * 0.03}) 0px, transparent 1px, transparent 59px, rgba(0,255,135,${opacity * 0.03}) 60px)`,
      backgroundSize: "60px 60px",
    }} />
  );
};

const SeriesLabel: React.FC = () => {
  const frame = useCurrentFrame();
  return (
    <div style={{
      position: "absolute", top: 30, left: SZ.sides,
      opacity: fadeIn(frame, 0, 10),
      fontFamily: FONT_MONO, fontSize: 13, fontWeight: 500, letterSpacing: 2,
      textTransform: "uppercase", color: T.dark.muted,
    }}>CONTENT OS</div>
  );
};

const Badge: React.FC<{text: string; delay?: number}> = ({text, delay = 5}) => {
  const frame = useCurrentFrame();
  return (
    <div style={{
      opacity: fadeIn(frame, delay, delay + 15),
      transform: `translateX(${interpolate(frame, [delay, delay + 15], [-30, 0], {extrapolateRight: CLAMP, easing: Easing.out(Easing.ease)})}px)`,
      background: T.accent, borderRadius: 4, padding: "8px 18px",
      display: "inline-flex", alignItems: "center", marginBottom: 20,
    }}>
      <span style={{fontFamily: FONT_SANS, fontSize: 15, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", color: "#000000"}}>{text}</span>
    </div>
  );
};

const Footer: React.FC<{slide?: string}> = ({slide = "10/10"}) => {
  const frame = useCurrentFrame();
  return (
    <div style={{
      position: "absolute", bottom: 40, left: SZ.sides, right: SZ.sides,
      display: "flex", justifyContent: "flex-end", alignItems: "center",
      opacity: fadeIn(frame, 40, 50),
    }}>
      <span style={{fontFamily: FONT_SANS, fontSize: 13, fontWeight: 500, color: T.dark.muted}}>{slide}</span>
    </div>
  );
};

export const OS10CTA: React.FC = () => {
  const frame = useCurrentFrame();
  const photoScale = interpolate(frame, [0, 20], [0.8, 1], {extrapolateRight: CLAMP, easing: Easing.out(Easing.ease)});
  const ringOpacity = interpolate(frame, [10, 30], [0, 1], {extrapolateRight: CLAMP});

  return (
    <AbsoluteFill style={{backgroundColor: T.dark.bg}}>
      <Sequence from={0} durationInFrames={300}>
        <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.sides}px ${SZ.bottom}px`}}>
          <GridPattern />
          <CircuitPattern />
          <SeriesLabel />

          {/* Photo with rings */}
          <div style={{position: "relative", marginBottom: 32}}>
            {/* Ring 3 — outermost */}
            <div style={{
              position: "absolute",
              top: "50%", left: "50%",
              width: 200, height: 200,
              marginTop: -100, marginLeft: -100,
              borderRadius: "50%",
              border: `1px solid rgba(0,255,135,${ringOpacity * 0.2})`,
              transform: `scale(${interpolate(frame, [10, 30], [0.7, 1], {extrapolateRight: CLAMP})})`,
            }} />
            {/* Ring 2 */}
            <div style={{
              position: "absolute",
              top: "50%", left: "50%",
              width: 160, height: 160,
              marginTop: -80, marginLeft: -80,
              borderRadius: "50%",
              border: `1px solid rgba(0,255,135,${ringOpacity * 0.35})`,
              transform: `scale(${interpolate(frame, [5, 25], [0.7, 1], {extrapolateRight: CLAMP})})`,
            }} />
            {/* Ring 1 — innermost */}
            <div style={{
              position: "absolute",
              top: "50%", left: "50%",
              width: 126, height: 126,
              marginTop: -63, marginLeft: -63,
              borderRadius: "50%",
              border: `2px solid rgba(0,255,135,${ringOpacity * 0.6})`,
              transform: `scale(${interpolate(frame, [0, 20], [0.7, 1], {extrapolateRight: CLAMP})})`,
            }} />
            {/* Photo */}
            <div style={{
              width: 110, height: 110, borderRadius: "50%", overflow: "hidden",
              transform: `scale(${photoScale})`,
              border: `3px solid ${T.accent}`,
              boxShadow: `0 0 30px rgba(0,255,135,0.25)`,
            }}>
              <Img
                src={staticFile("assets/profiles/guilhermeribeiro-me.jpg")}
                style={{width: "100%", height: "100%", objectFit: "cover"}}
              />
            </div>
          </div>

          {/* Centered content */}
          <div style={{
            opacity: fadeIn(frame, 25, 35),
            transform: `translateY(${slideUp(frame, 25, 35)}px)`,
            fontFamily: FONT_SANS, fontSize: 15, fontWeight: 600, color: T.accent,
            letterSpacing: 0.5, marginBottom: 16,
          }}>
            @guilhermeribeiro.me
          </div>

          <div style={{
            opacity: fadeIn(frame, 30, 45),
            transform: `translateY(${slideUp(frame, 30, 45)}px)`,
            fontFamily: FONT_SERIF, fontSize: 44, fontWeight: 700, lineHeight: 1.15,
            color: T.dark.headline, textAlign: "center", maxWidth: CW, marginBottom: 16,
          }}>
            Comenta OS — te mando o mapa.
          </div>

          <div style={{
            opacity: fadeIn(frame, 40, 52),
            transform: `translateY(${slideUp(frame, 40, 52)}px)`,
            fontFamily: FONT_SERIF, fontSize: 20, fontWeight: 400, lineHeight: 1.4,
            color: T.dark.body, textAlign: "center", maxWidth: CW, marginBottom: 28,
          }}>
            research → copy → social, sem contratar uma equipe.
          </div>

          <Badge text="COMENTA  OS" delay={50} />

          <Footer slide="10/10" />
        </AbsoluteFill>
      </Sequence>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
